/*
 * Copyright (c) 2026, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you've
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import { Box, useTheme } from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import Button from 'components/ChoreoSystem/Button/Button';
import { logoBlack } from 'utils/logos';
import useStyles from './PublicHeader.styles';

interface PublicHeaderProps {
  onLoginClick: () => void;
  onSignUpClick: () => void;
}

/**
 * The header of the unauthenticated landing page.
 *
 * Separate from `layouts/components/Header`, which is built for a signed in
 * user and reads the project, subscription and Copilot contexts — none of which
 * exist this side of authentication.
 *
 * Region switching is not offered here — it lives inside the sign in / sign up
 * dialog's auth card.
 */
export default function PublicHeader(props: PublicHeaderProps) {
  const { onLoginClick, onSignUpClick } = props;
  const classes = useStyles();
  const theme = useTheme();

  return (
    <Box component="header" className={classes.publicHeader}>
      <img
        alt={logoBlack.alt}
        src={logoBlack.src}
        width={theme.spacing(25)}
        className={classes.logo}
      />

      <Box
        display="flex"
        alignItems="center"
        gridGap={theme.spacing(1.5)}
        flexShrink={0}
      >
        <Button
          testId="public-header-login"
          variant="text"
          color="secondary"
          onClick={onLoginClick}
        >
          <FormattedMessage
            id="modules.login.publicHeader.login"
            defaultMessage="Log in"
          />
        </Button>
        <Button
          testId="public-header-signup"
          variant="contained"
          onClick={onSignUpClick}
        >
          <FormattedMessage
            id="modules.login.publicHeader.signUp"
            defaultMessage="Sign up"
          />
        </Button>
      </Box>
    </Box>
  );
}
